import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, EmptyState } from '@mustauto/ui/web';
import { api } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAuth } from '@/hooks/useAuth';
import type { WSEvent } from '@/lib/ws';
import { MessageSquare, Send, Loader2 } from 'lucide-react';

interface ChatMessage {
  id: string;
  senderId: string;
  senderRole: string;
  senderName: string;
  content: string | null;
  audioS3Key: string | null;
  audioDurationSeconds: number | null;
  readAt: string | null;
  createdAt: string;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAudioDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const ROLE_LABELS: Record<string, string> = {
  mechanic: 'Mecânico',
  manager: 'Gestor',
  b2c_customer: 'Cliente',
};

interface ChatPanelProps {
  osId: string;
  readOnly?: boolean;
}

export function ChatPanel({ osId, readOnly = false }: ChatPanelProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get(`/service-orders/${osId}/chat`);
      setMessages((res.data.messages as ChatMessage[]).reverse());
    } catch {
      console.warn('[ChatPanel] Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [osId]);

  useEffect(() => {
    setLoading(true);
    loadMessages();
    api.patch(`/service-orders/${osId}/chat/read`).catch(() => {});
  }, [osId, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Real-time updates via WebSocket
  const handleWSEvent = useCallback(
    (event: WSEvent) => {
      if (event.event === 'new-chat-message' && event.data.osId === osId) {
        loadMessages();
        api.patch(`/service-orders/${osId}/chat/read`).catch(() => {});
      }
    },
    [osId, loadMessages],
  );

  useWebSocket({
    workshopId: user?.workshopId ?? null,
    onEvent: handleWSEvent,
  });

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    const content = text.trim();
    setText('');
    setSending(true);
    try {
      const res = await api.post(`/service-orders/${osId}/chat`, { content });
      const newMsg: ChatMessage = {
        id: res.data.id,
        senderId: user?.id ?? '',
        senderRole: 'manager',
        senderName: 'Você',
        content,
        audioS3Key: null,
        audioDurationSeconds: null,
        readAt: null,
        createdAt: res.data.createdAt,
      };
      setMessages((prev) => [...prev, newMsg]);
    } catch {
      setText(content);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isWorkshopMessage = (msg: ChatMessage) =>
    msg.senderRole === 'mechanic' || msg.senderRole === 'manager';

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="border-b pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <MessageSquare size={18} />
          Chat da OS
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col overflow-hidden p-0">
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <EmptyState
              compact
              icon={<MessageSquare size={22} strokeWidth={1.5} />}
              title="Nenhuma mensagem"
              description="Escreva abaixo para falar com o cliente sobre esta OS."
            />
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => {
                const isMe = isWorkshopMessage(msg);
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                        isMe
                          ? 'rounded-br-sm bg-emerald-100 text-foreground dark:bg-emerald-950/40 dark:text-emerald-100'
                          : 'rounded-bl-sm bg-muted text-foreground'
                      }`}
                    >
                      {!isMe && (
                        <p className="mb-0.5 text-xs font-semibold text-blue-600 dark:text-blue-400">
                          {msg.senderName}{' '}
                          <span className="font-normal text-muted-foreground">
                            ({ROLE_LABELS[msg.senderRole] ?? msg.senderRole})
                          </span>
                        </p>
                      )}
                      {isMe && msg.senderRole === 'mechanic' && (
                        <p className="mb-0.5 text-xs font-semibold text-orange-600 dark:text-orange-400">
                          {msg.senderName} (Mecânico)
                        </p>
                      )}
                      {msg.content && (
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                      )}
                      {msg.audioS3Key && (
                        <p className="text-sm text-muted-foreground">
                          🎵 Audio{' '}
                          {msg.audioDurationSeconds
                            ? formatAudioDuration(msg.audioDurationSeconds)
                            : ''}
                        </p>
                      )}
                      <p
                        className={`mt-1 text-[10px] text-muted-foreground ${
                          isMe ? 'text-right' : ''
                        }`}
                      >
                        {formatTime(msg.createdAt)}
                        {isMe && msg.readAt && ' ✓✓'}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Input bar */}
        {!readOnly && (
          <div className="flex items-end gap-2 border-t p-3">
            <textarea
              className="flex-1 resize-none rounded-xl border bg-muted px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Responder como equipe..."
              rows={1}
              maxLength={2000}
            />
            <button
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white transition-colors hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500 disabled:bg-muted disabled:text-muted-foreground"
              onClick={handleSend}
              disabled={!text.trim() || sending}
            >
              {sending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
